/*
 * Mother of Macros: order minimum ($60 Local / $175 Nationwide by ZIP).
 *
 * One rule for every place a shopper can check out:
 *   - progress bar in [data-mom-min-bar] (one-time menu sticky bar, cart drawer, cart page),
 *   - checkout buttons stay disabled until the minimum is met:
 *     #CartDrawer-Checkout, the cart page checkout, dynamic checkout buttons, and any
 *     [data-mom-min-checkout] button; a capture-phase guard blocks clicks/submits too.
 *
 * Which carts it applies to: any cart with a normal (non gift card) item and no
 * subscription line. Subscription boxes keep their own meal-count rule (ats.js).
 * What counts: every such line except the Freeze Pack and gift cards (entrées and
 * desserts both count). ZIP: buildabox_zip cookie, else the customer's address ZIP;
 * a ZIP that is not Local (or no ZIP) uses the Nationwide minimum.
 *
 * Config comes from layout/theme.liquid (window.MOM_MINIMUM).
 */
(function () {
  'use strict';

  if (window.MOMMinimum) return;

  var config = window.MOM_MINIMUM || {};
  var FREEZE_VARIANT_ID = 40914461425700;
  var localZips = String(config.localZips || '').split(',').map(function (zip) { return zip.trim(); }).filter(Boolean);
  var moneyFormat = config.moneyFormat || '${{amount}}';
  var cart = null;
  var listeners = [];
  var refreshTimer = null;

  /* ---------- rule ---------- */

  function getZip() {
    var match = document.cookie.match(/(?:^|;\s*)buildabox_zip=([^;]*)/);
    var zip = match ? decodeURIComponent(match[1]).trim() : '';
    return zip || String(config.customerZip || '').trim();
  }

  function isLocal() {
    return localZips.indexOf(getZip()) !== -1;
  }

  function minimumCents() {
    return isLocal() ? (parseInt(config.local, 10) || 6000) : (parseInt(config.nationwide, 10) || 17500);
  }

  function isFreezePack(item) {
    return Number(item.variant_id) === FREEZE_VARIANT_ID;
  }

  // { applies, total, minimum, met } for a /cart.js object.
  function evaluate(cartData) {
    var items = (cartData && cartData.items) || [];
    var hasSubscription = items.some(function (item) { return item.selling_plan_allocation; });
    var counted = items.filter(function (item) { return !isFreezePack(item) && !item.gift_card; });
    var total = counted.reduce(function (sum, item) { return sum + (item.final_line_price || 0); }, 0);
    var minimum = minimumCents();
    return {
      applies: counted.length > 0 && !hasSubscription,
      total: total,
      minimum: minimum,
      met: total >= minimum
    };
  }

  /* ---------- money ---------- */

  function formatMoney(cents, format) {
    format = format || moneyFormat;
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    var type = match ? match[1] : 'amount';
    function delimit(decimals, thousands, decimal) {
      var parts = (Math.round(cents) / 100).toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    }
    var value;
    switch (type) {
      case 'amount_no_decimals': value = delimit(0, ',', '.'); break;
      case 'amount_with_comma_separator': value = delimit(2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = delimit(0, '.', ','); break;
      default: value = delimit(2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  // "$42" for whole dollars, "$41.98" otherwise.
  function formatShort(cents) {
    if (Math.round(cents) % 100 === 0) {
      return formatMoney(cents, moneyFormat.replace(/\{\{\s*amount\s*\}\}/, '{{amount_no_decimals}}'));
    }
    return formatMoney(cents);
  }

  /* ---------- progress bar ---------- */

  // Renders the bar into one [data-mom-min-bar] element for a total (cents).
  function renderBar(element, total, minimum) {
    if (!element) return;
    minimum = minimum || minimumCents();
    var met = total >= minimum;
    var percent = minimum > 0 ? Math.min(100, Math.round((total / minimum) * 100)) : 100;

    if (!element.firstElementChild) {
      element.classList.add('mom-min-bar');
      element.innerHTML =
        '<p class="mom-min-bar__text" aria-live="polite"></p>' +
        '<div class="mom-min-bar__track" role="progressbar" aria-valuemin="0" aria-valuemax="100">' +
        '<div class="mom-min-bar__fill"></div></div>';
    }
    element.classList.toggle('is-met', met);
    element.querySelector('.mom-min-bar__text').textContent = met
      ? '✓ Minimum reached – you’re ready to checkout!'
      : formatShort(total) + ' of ' + formatShort(minimum) + ' minimum – add ' + formatShort(minimum - total) + ' more';
    var track = element.querySelector('.mom-min-bar__track');
    track.setAttribute('aria-valuenow', String(percent));
    element.querySelector('.mom-min-bar__fill').style.width = percent + '%';
  }

  /* ---------- checkout lock ---------- */

  var CHECKOUT_SELECTOR = '#CartDrawer-Checkout, #checkout, .cart__checkout-button, [data-mom-min-checkout]';
  var DYNAMIC_SELECTOR = '.cart__dynamic-checkout-buttons, .additional-checkout-buttons';

  function blocked() {
    if (!cart) return false;
    var result = evaluate(cart);
    return result.applies && !result.met;
  }

  function applyLock() {
    var isBlocked = blocked();
    var buttons = document.querySelectorAll(CHECKOUT_SELECTOR);
    for (var i = 0; i < buttons.length; i++) {
      var button = buttons[i];
      // The one-time menu manages its own button from its live (optimistic) total.
      if (button.hasAttribute('data-mom-min-self')) continue;
      if (isBlocked) {
        if (button.dataset.momMinDisabled !== 'true') {
          button.dataset.momMinPreviouslyDisabled = button.disabled ? 'true' : 'false';
        }
        button.dataset.momMinDisabled = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
      } else if (button.dataset.momMinDisabled === 'true') {
        var wasDisabled = button.dataset.momMinPreviouslyDisabled === 'true';
        delete button.dataset.momMinDisabled;
        delete button.dataset.momMinPreviouslyDisabled;
        if (!wasDisabled && button.dataset.momFreezeOnlyDisabled !== 'true' && button.dataset.momMealThresholdDisabled !== 'true') {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
      }
    }
    var dynamic = document.querySelectorAll(DYNAMIC_SELECTOR);
    for (var j = 0; j < dynamic.length; j++) dynamic[j].classList.toggle('mom-min-hidden', isBlocked);
  }

  function renderAll() {
    var bars = document.querySelectorAll('[data-mom-min-bar]:not([data-mom-min-self])');
    var result = cart ? evaluate(cart) : null;
    for (var i = 0; i < bars.length; i++) {
      bars[i].hidden = !result || !result.applies;
      if (result && result.applies) renderBar(bars[i], result.total, result.minimum);
    }
    applyLock();
  }

  function isCheckoutTarget(target) {
    if (!target || typeof target.closest !== 'function') return false;
    if (target.closest(CHECKOUT_SELECTOR + ', ' + DYNAMIC_SELECTOR)) return true;
    var link = target.closest('a[href]');
    return Boolean(link && /\/checkout(s)?(\/|$|\?)/.test(link.getAttribute('href') || ''));
  }

  // Hard guard: no click or submit reaches checkout below the minimum.
  document.addEventListener('click', function (event) {
    if (!blocked() || !isCheckoutTarget(event.target)) return;
    var self = event.target.closest('[data-mom-min-self]');
    if (self) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    applyLock();
  }, true);

  document.addEventListener('submit', function (event) {
    if (!blocked()) return;
    var form = event.target;
    var submitter = event.submitter;
    var goesToCheckout = (submitter && submitter.name === 'checkout') ||
      (form && form.querySelector && form.querySelector('[name="checkout"]') && !(submitter && submitter.name === 'update'));
    if (!goesToCheckout) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    applyLock();
  }, true);

  /* ---------- cart state ---------- */

  function refresh() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        cart = data;
        renderAll();
        listeners.forEach(function (fn) { try { fn(data); } catch (error) { /* listener errors stay local */ } });
        return data;
      })
      .catch(function () { /* keep the last known state */ });
  }

  function scheduleRefresh() {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refresh, 150);
  }

  // Refresh after any successful cart change made by any script.
  var previousFetch = window.fetch;
  window.fetch = function (input) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var promise = previousFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url)) {
      promise.then(function (response) { if (response && response.ok) scheduleRefresh(); }).catch(function () {});
    }
    return promise;
  };

  // Cart drawer / cart page HTML is replaced after updates: re-apply bars and locks.
  function observe(selector) {
    var element = document.querySelector(selector);
    if (!element || typeof MutationObserver !== 'function') return;
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(function () { pending = false; renderAll(); });
    }).observe(element, { childList: true, subtree: true });
  }

  function start() {
    observe('cart-drawer');
    observe('#main-cart-footer');
    observe('cart-items');
    refresh();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  window.addEventListener('pageshow', function (event) { if (event.persisted) refresh(); });

  window.MOMMinimum = {
    getZip: getZip,
    isLocal: isLocal,
    minimumCents: minimumCents,
    evaluate: evaluate,
    renderBar: renderBar,
    formatMoney: formatMoney,
    formatShort: formatShort,
    refresh: refresh,
    getCart: function () { return cart; },
    onChange: function (fn) { listeners.push(fn); if (cart) fn(cart); }
  };
})();
